/**
 * Theme preference: persisted to localStorage, applied to <html>.
 *
 * One signal — `[data-bs-theme]` on <html> — drives both Hawk's own chrome
 * (via Tailwind's `dark:` variant; see index.css) and the embedded
 * Inspect/Scout viewers (which set their own colors off `[data-bs-theme]`
 * and the `vscode-dark` body class). Keeping them coupled to the same
 * attribute is the whole reason this is one tiny module.
 *
 * The preference is two-dimensional:
 *   - mode: system | light | dark   — resolves to a dark/light boolean
 *   - eventColors: bool             — viewer-internal "readable" variant
 *                                     (distinct backgrounds for user /
 *                                     assistant / tool messages)
 *
 * Storage matches what the embedded viewers expect: a single string at
 * `<key>.state.themePreference` encoding both dimensions ("light", "dark",
 * "system", "readable-light", ...). The two viewers persist it under separate
 * keys (Inspect: `inspect-view-user-settings`, Scout:
 * `inspect-scout-user-settings`) because each key's blob also holds that
 * viewer's other settings — we can't collapse them into one key.
 *
 * The THEME itself is a SINGLETON per browser, though: Hawk's picker and each
 * viewer's own picker are separate components but all drive one value. Each
 * picker writes only the key it knows (Hawk writes both; a viewer writes its
 * own) and then calls `__APPLY_BROWSER_THEME__`. On every apply we detect which
 * key changed and mirror that value across all keys, so every picker reflects
 * the same theme and all routes render identically. See `resolveAndSync`.
 */

export type ThemeMode = 'system' | 'light' | 'dark';
export interface ThemePreference {
  mode: ThemeMode;
  eventColors: boolean;
}

// Default when nothing valid is stored, and the target of Reset. Event Colors
// (the readable variant) is on by default per product decision — keep in sync
// with the viewers' own defaults (their userSettings + @tsmono/theme).
export const DEFAULT_PREFERENCE: ThemePreference = {
  mode: 'system',
  eventColors: true,
};

// localStorage keys the two embedded viewers persist their (identically
// shaped) settings under. The themePreference field is kept in sync across all
// of them (see resolveAndSync); order only sets which is canonical on cold read.
const INSPECT_STORAGE_KEY = 'inspect-view-user-settings';
const SCOUT_STORAGE_KEY = 'inspect-scout-user-settings';
const STORAGE_KEYS = [INSPECT_STORAGE_KEY, SCOUT_STORAGE_KEY] as const;

// The encoded value last mirrored across all keys. Lets us detect which key a
// viewer's own picker just changed, so we mirror that change to the others.
let lastSynced: string | null = null;

const encode = (p: ThemePreference): string =>
  p.eventColors ? `readable-${p.mode}` : p.mode;

// `raw` is always a stored string here (readEncoded returns string | null and
// callers guard on non-null), so no need to default a non-string case.
const decode = (raw: string): ThemePreference => {
  const readable = raw.startsWith('readable-');
  const modePart = readable ? raw.slice('readable-'.length) : raw;
  const mode: ThemeMode =
    modePart === 'light' || modePart === 'dark' ? modePart : 'system';
  // If the stored value indicated readable but the mode part was garbled
  // (e.g. "readable-" or "readable-foo"), preserve the readable intent
  // rather than silently dropping it.
  return { mode, eventColors: readable };
};

// The encoded themePreference under a key, or null if the key has no theme set
// (it may still exist holding the viewer's other settings).
const readEncoded = (key: string): string | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { themePreference?: unknown } };
    const stored = parsed?.state?.themePreference;
    return typeof stored === 'string' && stored ? stored : null;
  } catch {
    return null;
  }
};

// Write the encoded themePreference into a key, preserving any other state the
// viewer persists alongside it (including `version`, so a future viewer
// migration isn't silently downgraded).
const writeEncoded = (key: string, encoded: string): void => {
  let payload: { state: Record<string, unknown>; version?: number };
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    payload =
      parsed &&
      typeof parsed === 'object' &&
      'state' in parsed &&
      parsed.state &&
      typeof parsed.state === 'object'
        ? (parsed as typeof payload)
        : { state: {}, version: 0 };
  } catch {
    payload = { state: {}, version: 0 };
  }
  payload.state.themePreference = encoded;
  localStorage.setItem(key, JSON.stringify(payload));
};

export const readPreference = (): ThemePreference => {
  // Keys are kept in sync (see resolveAndSync), so the first key with a value
  // is the singleton preference.
  for (const key of STORAGE_KEYS) {
    const stored = readEncoded(key);
    if (stored) return decode(stored);
  }
  return { ...DEFAULT_PREFERENCE };
};

export const writePreference = (next: ThemePreference): void => {
  // Hawk's own picker writes the singleton value to every key directly.
  const encoded = encode(next);
  for (const key of STORAGE_KEYS) writeEncoded(key, encoded);
  lastSynced = encoded;
};

// Resolve the singleton preference and mirror it across all keys. Mirroring is
// what makes the theme one-per-browser. The first key whose value differs from
// the last sync wins — in the normal case that's the single key a viewer's own
// picker just wrote. Ambiguous cases resolve deterministically to the first
// (inspect) key: on cold start (lastSynced === null) both keys look "changed"
// so a pre-existing divergent scout value yields to inspect once; and if two
// tabs each wrote a different key before this runs, inspect wins. OS flips and
// cross-tab no-op writes change nothing, so the current value is kept.
const resolveAndSync = (): ThemePreference => {
  const current = STORAGE_KEYS.map(readEncoded);
  // First key differing from the last sync wins (normally the one a viewer's
  // picker just wrote; on cold start / ties, the first/inspect key).
  let chosen = current.find(v => v !== null && v !== lastSynced) ?? null;
  // Nothing differs -> first key that has any value.
  if (chosen === null) chosen = current.find(v => v !== null) ?? null;

  const pref = chosen !== null ? decode(chosen) : { ...DEFAULT_PREFERENCE };
  const encoded = encode(pref);
  STORAGE_KEYS.forEach((key, i) => {
    if (current[i] !== encoded) writeEncoded(key, encoded);
  });
  lastSynced = encoded;
  return pref;
};

export const resolveIsDark = (mode: ThemeMode): boolean => {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

export const applyTheme = (): void => {
  const pref = resolveAndSync();
  const dark = resolveIsDark(pref.mode);
  const html = document.documentElement;
  html.setAttribute('data-bs-theme', dark ? 'dark' : 'light');
  // Event Colors (readable variant). The embedded viewer reads
  // `[data-theme-variant="readable"]`.
  if (pref.eventColors) {
    html.setAttribute('data-theme-variant', 'readable');
  } else {
    html.removeAttribute('data-theme-variant');
  }
  // The embedded viewer keys prism code-block syntax highlighting and a few
  // tool-output rules off `.vscode-dark` (not the data attribute), so we
  // toggle it on <body> to keep code blocks dark in dark mode. The viewer's
  // base.css maps `--vscode-* → --inspect-*` (its semantic design tokens) for
  // `body[class^="vscode-"]`, and its dark token values live under
  // `:root[data-bs-theme="dark"]`, so the mapping resolves to dark values
  // rather than falling back to white.
  document.body.classList.toggle('vscode-dark', dark);
};

/**
 * Notify when the stored preference changes in another tab. Same-tab changes
 * are made by the caller, which already knows; this only covers cross-tab
 * writes to either viewer's key. Returns an unsubscribe.
 *
 * For resolved dark/light, prefer `useIsDarkTheme` (it observes the applied
 * `[data-bs-theme]`, the single source of truth). Use this only when you need
 * the full preference (mode incl. "system", event colors), e.g. the picker.
 */
export const subscribePreferenceChange = (
  onChange: () => void
): (() => void) => {
  const handler = (e: StorageEvent) => {
    if (e.key && (STORAGE_KEYS as readonly string[]).includes(e.key)) {
      onChange();
    }
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
};

declare global {
  interface Window {
    // Each embedded viewer's theme picker (Inspect's navbar on /eval-set, and
    // Scout's on /scan) commits a new preference by writing its own
    // localStorage key and then calling this global. The viewers do NOT apply
    // `data-bs-theme` to <html> themselves — they rely on the host. Hawk owns
    // <html>, so we expose applyTheme here, which also mirrors the change
    // across the other key (see resolveAndSync) to keep the theme a singleton.
    __APPLY_BROWSER_THEME__?: () => void;
  }
}

export const installThemeBootstrap = (): void => {
  window.__APPLY_BROWSER_THEME__ = applyTheme;
  applyTheme();
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', applyTheme);
  // Cross-tab: another tab changed the preference (under either viewer's
  // key); re-apply in this one.
  window.addEventListener('storage', e => {
    if (e.key && (STORAGE_KEYS as readonly string[]).includes(e.key)) {
      applyTheme();
    }
  });
};
